import { useState } from'react';
import { motion, AnimatePresence } from'framer-motion';
import { ChevronUp, ChevronDown, Star, Lock, Unlock, Shield, Gift, Sparkles, Eye, Zap, Crown, Check, RotateCcw } from'lucide-react';
import { sevenHeavens } from'../../data/frameworks';
import { SefirotTree } from'../interactive/SefirotTree';

// Chariot component definitions for the builder
const chariotComponentsData = {
 creatures: [
 { id:'lion', name:'Lion', hebrew:'אריה', color:'#EA580C', direction:'South', element:'Fire', description:'Chesed - Divine Love and Expansion' },
 { id:'ox', name:'Ox', hebrew:'שור', color:'#22C55E', direction:'North', element:'Earth', description:'Gevurah - Divine Strength and Restraint' },
 { id:'eagle', name:'Eagle', hebrew:'נשר', color:'#3B82F6', direction:'East', element:'Air', description:'Tiferet - Divine Harmony and Balance' },
 { id:'human', name:'Human', hebrew:'אדם', color:'#7C3AED', direction:'West', element:'Water', description:'Malkhut - Divine Sovereignty' },
 ],
 wheels: [
 { id:'inner', name:'Inner Wheel', hebrew:'גלגל פנימי', eyes: 36, description:'The wheel of Divine Will' },
 { id:'middle', name:'Middle Wheel', hebrew:'גלגל אמצעי', eyes: 72, description:'The wheel of Divine Names' },
 { id:'outer', name:'Outer Wheel', hebrew:'גלגל חיצון', eyes: 108, description:'The wheel of Complete Vision' },
 ],
 throne: [
 { id:'sapphire', name:'Sapphire Foundation', hebrew:'ספיר', color:'#1E40AF', description:'The crystalline base of divine wisdom' },
 { id:'fire', name:'Fire Surrounding', hebrew:'אש', color:'#EA580C', description:'The consuming fire of transformation' },
 { id:'rainbow', name:'Rainbow Aura', hebrew:'קשת', colors: ['#EF4444','#F97316','#FBBF24','#22C55E','#3B82F6','#8B5CF6','#A855F7'], description:'The covenant of divine beauty' },
 ],
};

export default function Step5() {
 // Seven heavens state
 const [currentHeaven, setCurrentHeaven] = useState(1);
 const [visitedHeavens, setVisitedHeavens] = useState<number[]>([1]);

 // Chariot builder state
 const [buildMode, setBuildMode] = useState(false);
 const [selectedCreatures, setSelectedCreatures] = useState<string[]>([]);
 const [selectedWheels, setSelectedWheels] = useState<string[]>([]);
 const [selectedThrone, setSelectedThrone] = useState<string[]>([]);
 const [chariotComplete, setChariotComplete] = useState(false);
 const [assemblyStage, setAssemblyStage] = useState(0);

 const ascend = () => {
 if (currentHeaven < 7) {
 const newHeaven = currentHeaven + 1;
 setCurrentHeaven(newHeaven);
 if (!visitedHeavens.includes(newHeaven)) {
 setVisitedHeavens([...visitedHeavens, newHeaven]);
 }
 }
 };

 const descend = () => {
 if (currentHeaven > 1) {
 setCurrentHeaven(currentHeaven - 1);
 }
 };

 const goToHeaven = (num: number) => {
 if (visitedHeavens.includes(num) || num === visitedHeavens.length + 1) {
 setCurrentHeaven(num);
 if (!visitedHeavens.includes(num)) {
 setVisitedHeavens([...visitedHeavens, num]);
 }
 }
 };

 const heaven = sevenHeavens[currentHeaven - 1];

 // Color gradient based on current heaven level
 const getHeavenColor = (level: number) => {
 const colors = [
'from-gray-600 to-gray-800', // Vilon
'from-blue-900 to-indigo-900', // Rakia
'from-purple-800 to-indigo-800', // Shchakim
'from-purple-700 to-violet-800', // Zebul
'from-violet-600 to-purple-700', // Ma'on
'from-amber-600 to-orange-700', // Machon
'from-amber-400 to-yellow-500', // Aravot
 ];
 return colors[level - 1] || colors[0];
 };

 // Toggle creature selection
 const toggleCreature = (id: string) => {
 if (selectedCreatures.includes(id)) {
 setSelectedCreatures(selectedCreatures.filter(c => c !== id));
 } else {
 setSelectedCreatures([...selectedCreatures, id]);
 }
 };

 // Toggle wheel selection
 const toggleWheel = (id: string) => {
 if (selectedWheels.includes(id)) {
 setSelectedWheels(selectedWheels.filter(w => w !== id));
 } else {
 setSelectedWheels([...selectedWheels, id]);
 }
 };

 // Toggle throne selection
 const toggleThrone = (id: string) => {
 if (selectedThrone.includes(id)) {
 setSelectedThrone(selectedThrone.filter(t => t !== id));
 } else {
 setSelectedThrone([...selectedThrone, id]);
 }
 };

 // Check if chariot can be assembled
 const canAssemble = selectedCreatures.length === 4 && selectedWheels.length >= 1 && selectedThrone.length >= 1;

 // Start assembly animation
 const startAssembly = () => {
 if (!canAssemble) return;
 setAssemblyStage(1);
 setTimeout(() => setAssemblyStage(2), 1500);
 setTimeout(() => setAssemblyStage(3), 3000);
 setTimeout(() => {
 setAssemblyStage(4);
 setChariotComplete(true);
 }, 4500);
 };

 // Reset builder
 const resetBuilder = () => {
 setSelectedCreatures([]);
 setSelectedWheels([]);
 setSelectedThrone([]);
 setChariotComplete(false);
 setAssemblyStage(0);
 };

 return (
 <div className="section-container relative">
 {/* Introduction */}
 <div className="max-w-4xl mx-auto mb-12 relative z-10">
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="text-center mb-8"
 >
 <p className="hebrew-text text-2xl md:text-3xl text-primary-600 dark:text-primary-400 mb-4">
 שבעה רקיעים בראם הקדוש ברוך הוא
 </p>
 <p className="text-lg text-[var(--color-muted)] italic">
"Seven heavens did the Holy One, blessed be He, create..."
 </p>
 <p className="text-sm text-[var(--color-muted)] mt-2">- Talmud, Chagigah 12b</p>
 </motion.div>

 {/* Mode Toggle */}
 <div className="flex justify-center gap-4 mb-8">
 <button
 onClick={() => setBuildMode(false)}
 className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
 !buildMode
 ?'bg-gradient-to-r from-primary-600 to-secondary-700 text-white shadow-lg'
 :'bg-[var(--color-card)] border border-[var(--color-border)] hover:border-primary-500'
 }`}
 >
 <Star className="w-5 h-5" />
 Seven Heavens Journey
 </button>
 <button
 onClick={() => setBuildMode(true)}
 className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
 buildMode
 ?'bg-gradient-to-r from-primary-600 to-secondary-700 text-white shadow-lg'
 :'bg-[var(--color-card)] border border-[var(--color-border)] hover:border-primary-500'
 }`}
 >
 <Sparkles className="w-5 h-5" />
 Build Your Chariot
 </button>
 </div>
 </div>

 {/* Chariot Builder Mode */}
 <AnimatePresence mode="wait">
 {buildMode ? (
 <motion.div
 key="builder"
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 exit={{ opacity: 0, y: -20 }}
 className="max-w-6xl mx-auto relative z-10"
 >
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
 {/* Component Selection */}
 <div className="space-y-6">
 {/* Creatures Selection */}
 <div className="card transition-all duration-300">
 <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
 <Zap className="w-5 h-5 text-primary-600" />
 Select Four Chayot (Living Creatures)
 <span className="text-sm font-normal text-[var(--color-muted)]">
 ({selectedCreatures.length}/4)
 </span>
 </h3>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
 {chariotComponentsData.creatures.map((creature) => (
 <button
 key={creature.id}
 onClick={() => toggleCreature(creature.id)}
 disabled={assemblyStage > 0}
 className={`p-4 rounded-xl border-2 text-left transition-all duration-300 relative ${
 selectedCreatures.includes(creature.id)
 ?'border-primary-500 bg-primary-500/10'
 :'border-[var(--color-border)] hover:border-primary-500/50'
 } ${assemblyStage > 0 ?'opacity-50 cursor-not-allowed' :'hover:shadow-md'}`}
 >
 <div className="flex items-center gap-3">
 <div
 className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300"
 style={{ backgroundColor: `${creature.color}20`, borderColor: creature.color, borderWidth: 2 }}
 >
 <span className="text-lg">{creature.hebrew}</span>
 </div>
 <div>
 <p className="font-medium">{creature.name}</p>
 <p className="text-xs text-[var(--color-muted)]">{creature.direction} / {creature.element}</p>
 </div>
 </div>
 {selectedCreatures.includes(creature.id) && (
 <Check className="absolute top-2 right-2 w-5 h-5 text-primary-600" />
 )}
 </button>
 ))}
 </div>
 </div>

 {/* Wheels Selection */}
 <div className="card transition-all duration-300">
 <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
 <Eye className="w-5 h-5 text-secondary-600" />
 Select Ophanim (Wheels)
 <span className="text-sm font-normal text-[var(--color-muted)]">
 ({selectedWheels.length} selected)
 </span>
 </h3>
 <div className="space-y-3">
 {chariotComponentsData.wheels.map((wheel) => (
 <button
 key={wheel.id}
 onClick={() => toggleWheel(wheel.id)}
 disabled={assemblyStage > 0}
 className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-300 ${
 selectedWheels.includes(wheel.id)
 ?'border-secondary-500 bg-secondary-500/10'
 :'border-[var(--color-border)] hover:border-secondary-500/50'
 } ${assemblyStage > 0 ?'opacity-50 cursor-not-allowed' :'hover:shadow-md'}`}
 >
 <div className="flex items-center justify-between">
 <div>
 <p className="font-medium">{wheel.name}</p>
 <p className="text-sm text-[var(--color-muted)]">{wheel.description}</p>
 </div>
 <div className="text-right">
 <p className="hebrew-text text-lg">{wheel.hebrew}</p>
 <p className="text-xs text-[var(--color-muted)]">{wheel.eyes} eyes</p>
 </div>
 </div>
 </button>
 ))}
 </div>
 </div>

 {/* Throne Selection */}
 <div className="card transition-all duration-300">
 <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
 <Crown className="w-5 h-5 text-[#3366cc]" />
 Select Throne Elements
 <span className="text-sm font-normal text-[var(--color-muted)]">
 ({selectedThrone.length} selected)
 </span>
 </h3>
 <div className="space-y-3">
 {chariotComponentsData.throne.map((element) => (
 <button
 key={element.id}
 onClick={() => toggleThrone(element.id)}
 disabled={assemblyStage > 0}
 className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-300 ${
 selectedThrone.includes(element.id)
 ?'border-amber-500 bg-[#3366cc]/10'
 :'border-[var(--color-border)] hover:border-amber-500/50'
 } ${assemblyStage > 0 ?'opacity-50 cursor-not-allowed' :'hover:shadow-md'}`}
 >
 <div className="flex items-center gap-3">
 <div
 className="w-8 h-8 rounded-lg transition-all duration-300"
 style={{
 background: element.id ==='rainbow'
 ?'linear-gradient(90deg, #EF4444, #F97316, #FBBF24, #22C55E, #3B82F6, #8B5CF6, #A855F7)'
 : element.color,
 }}
 />
 <div>
 <p className="font-medium">{element.name}</p>
 <p className="text-sm text-[var(--color-muted)]">{element.description}</p>
 </div>
 </div>
 </button>
 ))}
 </div>
 </div>

 {/* Assembly Controls */}
 <div className="flex gap-4">
 <button
 onClick={startAssembly}
 disabled={!canAssemble || assemblyStage > 0}
 className={`flex-1 py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${
 canAssemble && assemblyStage === 0
 ?'bg-gradient-to-r from-primary-600 to-secondary-700 text-white hover:shadow-lg'
 :'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
 }`}
 >
 <Sparkles className="w-5 h-5" />
 {assemblyStage > 0 ?'Assembling...' :'Assemble Chariot'}
 </button>
 <button
 onClick={resetBuilder}
 className="px-6 py-4 rounded-xl border border-[var(--color-border)] hover:bg-[var(--color-card)] transition-all duration-300"
 >
 <RotateCcw className="w-5 h-5" />
 </button>
 </div>
 </div>

 {/* Chariot Visualization */}
 <div className="card transition-all duration-300">
 <h3 className="text-lg font-semibold mb-4 text-center">Your Divine Chariot</h3>
 <div className="relative bg-gradient-to-b from-[#0f172a] to-[#1e1b4b] rounded-xl overflow-hidden p-8 min-h-[400px]">
 {/* Card-based chariot visualization */}
 <div className="flex flex-col items-center justify-center h-full gap-6">
 {/* Throne elements */}
 {(assemblyStage >= 3 || chariotComplete) && selectedThrone.length > 0 && (
 <motion.div
 initial={{ opacity: 0, y: -20 }}
 animate={{ opacity: 1, y: 0 }}
 className="flex flex-wrap justify-center gap-2"
 >
 {selectedThrone.map((id) => {
 const throne = chariotComponentsData.throne.find(t => t.id === id);
 return throne ? (
 <div
 key={id}
 className="px-4 py-2 rounded-lg text-white text-sm font-medium"
 style={{
 background: id ==='rainbow'
 ?'linear-gradient(90deg, #EF4444, #F97316, #FBBF24, #22C55E, #3B82F6)'
 : throne.color,
 }}
 >
 {throne.name}
 </div>
 ) : null;
 })}
 </motion.div>
 )}

 {/* Kavod indicator */}
 {(assemblyStage >= 4 || chariotComplete) && (
 <motion.div
 initial={{ opacity: 0, scale: 0.5 }}
 animate={{ opacity: 1, scale: 1 }}
 className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/50"
 >
 <span className="hebrew-text text-xl text-white">כבוד</span>
 </motion.div>
 )}

 {/* Creatures */}
 {(assemblyStage >= 2 || chariotComplete) && selectedCreatures.length > 0 && (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 className="flex flex-wrap justify-center gap-3"
 >
 {selectedCreatures.map((id) => {
 const creature = chariotComponentsData.creatures.find(c => c.id === id);
 return creature ? (
 <div
 key={id}
 className="w-14 h-14 rounded-full flex items-center justify-center text-white text-lg shadow-lg transition-all duration-300"
 style={{ backgroundColor: creature.color }}
 >
 {creature.hebrew}
 </div>
 ) : null;
 })}
 </motion.div>
 )}

 {/* Wheels */}
 {(assemblyStage >= 1 || chariotComplete) && selectedWheels.length > 0 && (
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="flex flex-wrap justify-center gap-2"
 >
 {selectedWheels.map((id) => {
 const wheel = chariotComponentsData.wheels.find(w => w.id === id);
 return wheel ? (
 <div
 key={id}
 className="px-3 py-1 rounded-full border-2 border-amber-500 text-[#3366cc] text-xs"
 >
 <Eye className="w-3 h-3 inline mr-1" />
 {wheel.eyes} eyes
 </div>
 ) : null;
 })}
 </motion.div>
 )}

 {/* Empty state */}
 {assemblyStage === 0 && !chariotComplete && (
 <div className="text-center text-white/50">
 <p className="text-sm">Select components to build your chariot</p>
 </div>
 )}
 </div>

 {/* Assembly stage indicators */}
 <div className="absolute bottom-4 left-4 right-4">
 <div className="flex justify-between gap-2">
 {['Wheels','Creatures','Throne','Kavod'].map((stage, i) => (
 <div
 key={stage}
 className={`flex-1 text-center py-2 rounded-lg text-xs font-medium transition-all duration-300 ${
 assemblyStage > i || chariotComplete
 ?'bg-primary-600 text-white'
 : assemblyStage === i + 1
 ?'bg-primary-600/50 text-white animate-pulse'
 :'bg-white/10 text-white/50'
 }`}
 >
 {stage}
 </div>
 ))}
 </div>
 </div>
 </div>

 {/* Completion message */}
 <AnimatePresence>
 {chariotComplete && (
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="mt-6 p-6 rounded-xl bg-gradient-to-r from-primary-600/20 to-secondary-700/20 border border-primary-500/30 text-center"
 >
 <h4 className="text-xl font-semibold text-primary-600 dark:text-primary-400 mb-2">
 Baruch HaShem! Your Chariot is Complete!
 </h4>
 <p className="hebrew-text text-2xl mb-4">ברוך כבוד ה׳ ממקומו</p>
 <p className="text-[var(--color-muted)]">
"Blessed is the Glory of the Lord from His place" - The divine chariot bears
 the throne of glory, surrounded by the living creatures and wheels of divine vision.
 </p>
 </motion.div>
 )}
 </AnimatePresence>
 </div>
 </div>
 </motion.div>
 ) : (
 /* Seven Heavens Journey Mode */
 <motion.div
 key="heavens"
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 exit={{ opacity: 0, y: -20 }}
 className="max-w-5xl mx-auto relative z-10"
 >
 {/* Heavens Map Card */}
 <div className="card mb-8 transition-all duration-300">
 <h3 className="text-lg font-semibold mb-4 text-center">Map of the Seven Heavens</h3>
 <div className="relative bg-gradient-to-b from-[#0f172a] to-[#1e1b4b] rounded-xl overflow-hidden p-8">
 {/* Vertical stack of heavens */}
 <div className="flex flex-col items-center gap-2">
 {sevenHeavens.slice().reverse().map((h) => {
 const isVisited = visitedHeavens.includes(h.number);
 const isCurrent = currentHeaven === h.number;
 const width = 100 - (7 - h.number) * 10;

 return (
 <motion.button
 key={h.number}
 onClick={() => goToHeaven(h.number)}
 disabled={!isVisited && h.number !== visitedHeavens.length + 1}
 initial={{ opacity: 0, scale: 0.9 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ delay: (7 - h.number) * 0.1 }}
 className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-all duration-300 ${
 isCurrent ?'ring-2 ring-amber-400 shadow-lg' :''
 } ${!isVisited && h.number !== visitedHeavens.length + 1 ?'opacity-30 cursor-not-allowed' :'hover:scale-105'}`}
 style={{
 width: `${width}%`,
 background: isVisited
 ? `linear-gradient(135deg, var(--tw-gradient-stops))`
 :'#374151',
'--tw-gradient-from': h.number === 7 ?'#FBBF24' : h.number >= 5 ?'#7C3AED' :'#3B82F6',
'--tw-gradient-to': h.number === 7 ?'#F97316' : h.number >= 5 ?'#4C1D95' :'#1E3A8A',
 } as React.CSSProperties}
 >
 <span className="mr-2">{h.number}.</span>
 {h.name}
 {isCurrent && <Star className="w-4 h-4 inline ml-2 text-amber-300" />}
 </motion.button>
 );
 })}
 </div>
 </div>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
 {/* Left: Heaven Levels */}
 <div className="lg:col-span-1">
 <div className="card sticky top-24 transition-all duration-300">
 <h3 className="text-lg font-semibold mb-4 text-center">Seven Heavens</h3>

 <div className="space-y-2">
 {sevenHeavens.slice().reverse().map((h) => {
 const isVisited = visitedHeavens.includes(h.number);
 const isCurrent = currentHeaven === h.number;
 const isNext = h.number === visitedHeavens.length + 1;

 return (
 <button
 key={h.number}
 onClick={() => goToHeaven(h.number)}
 disabled={!isVisited && !isNext}
 className={`w-full p-3 rounded-lg flex items-center gap-3 transition-all duration-300 ${
 isCurrent
 ?'bg-gradient-to-r from-primary-600 to-secondary-700 text-white shadow-lg'
 : isVisited
 ?'bg-[var(--color-background)] hover:bg-primary-600/10 border border-[var(--color-border)]'
 : isNext
 ?'bg-[var(--color-background)] border-2 border-dashed border-primary-600/50 hover:border-primary-600'
 :'bg-gray-100 dark:bg-gray-800/50 opacity-50 cursor-not-allowed'
 }`}
 >
 <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
 isCurrent
 ?'bg-white/20'
 : isVisited
 ?'bg-primary-600/20 text-primary-600'
 :'bg-gray-200 dark:bg-gray-700'
 }`}>
 {h.number}
 </div>
 <div className="text-left flex-1">
 <p className="font-medium text-sm">{h.name}</p>
 <p className="hebrew-text text-xs opacity-75">{h.hebrewName}</p>
 </div>
 {isVisited ? (
 <Unlock className="w-4 h-4 opacity-50" />
 ) : (
 <Lock className="w-4 h-4 opacity-50" />
 )}
 </button>
 );
 })}
 </div>

 {/* Progress */}
 <div className="mt-6 pt-4 border-t border-[var(--color-border)]">
 <p className="text-sm text-[var(--color-muted)] text-center">
 Progress: {visitedHeavens.length} / 7 heavens explored
 </p>
 <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full mt-2">
 <div
 className="h-full bg-gradient-to-r from-primary-600 to-secondary-700 rounded-full transition-all duration-300"
 style={{ width: `${(visitedHeavens.length / 7) * 100}%` }}
 />
 </div>
 </div>
 </div>
 </div>

 {/* Right: Current Heaven Details */}
 <div className="lg:col-span-2">
 <AnimatePresence mode="wait">
 <motion.div
 key={currentHeaven}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 exit={{ opacity: 0, y: -20 }}
 transition={{ duration: 0.3 }}
 >
 {/* Heaven Header */}
 <div className={`rounded-2xl p-8 bg-gradient-to-br ${getHeavenColor(currentHeaven)} text-white mb-8`}>
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-center gap-3">
 <Star className="w-8 h-8" />
 <div>
 <p className="text-sm opacity-75">Heaven {heaven.number} of 7</p>
 <h2 className="text-3xl font-display font-bold">{heaven.name}</h2>
 </div>
 </div>
 <p className="hebrew-text text-3xl">{heaven.hebrewName}</p>
 </div>

 <p className="text-lg opacity-90">{heaven.description}</p>

 <div className="mt-6 flex items-center gap-2">
 <Shield className="w-5 h-5" />
 <span className="text-sm">Guardian Angel: <strong>{heaven.ruler}</strong></span>
 </div>
 </div>

 {/* Challenges and Rewards */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
 <div className="card border-l-4 border-primary-600 transition-all duration-300 hover:shadow-lg">
 <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
 <Shield className="w-5 h-5 text-primary-600" />
 Challenges of Ascent
 </h3>
 <ul className="space-y-3">
 {heaven.challenges.map((challenge, index) => (
 <li key={index} className="flex items-start gap-2">
 <span className="w-6 h-6 rounded-full bg-primary-600/10 text-primary-600 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
 {index + 1}
 </span>
 <span className="text-[var(--color-muted)]">{challenge}</span>
 </li>
 ))}
 </ul>
 </div>

 <div className="card border-l-4 border-secondary-600 transition-all duration-300 hover:shadow-lg">
 <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
 <Gift className="w-5 h-5 text-secondary-600" />
 Rewards of Attainment
 </h3>
 <ul className="space-y-3">
 {heaven.rewards.map((reward, index) => (
 <li key={index} className="flex items-start gap-2">
 <span className="w-6 h-6 rounded-full bg-secondary-600/10 text-secondary-600 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
 {index + 1}
 </span>
 <span className="text-[var(--color-muted)]">{reward}</span>
 </li>
 ))}
 </ul>
 </div>
 </div>

 {/* Navigation Controls */}
 <div className="flex items-center justify-center gap-4">
 <button
 onClick={descend}
 disabled={currentHeaven === 1}
 className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-300 ${
 currentHeaven === 1
 ?'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
 :'bg-[var(--color-card)] border border-[var(--color-border)] hover:border-primary-600 hover:shadow-md'
 }`}
 >
 <ChevronDown className="w-5 h-5" />
 Descend
 </button>

 <div className="px-6 py-3 bg-gradient-to-r from-primary-600/10 to-secondary-700/10 rounded-lg">
 <p className="text-sm text-[var(--color-muted)]">Current Level</p>
 <p className="text-2xl font-bold text-center">{currentHeaven}</p>
 </div>

 <button
 onClick={ascend}
 disabled={currentHeaven === 7}
 className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-300 ${
 currentHeaven === 7
 ?'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
 :'bg-gradient-to-r from-primary-600 to-secondary-700 text-white hover:from-primary-500 hover:to-secondary-600 hover:shadow-lg'
 }`}
 >
 Ascend
 <ChevronUp className="w-5 h-5" />
 </button>
 </div>

 {/* Special Messages */}
 {currentHeaven === 7 && (
 <motion.div
 initial={{ opacity: 0, scale: 0.95 }}
 animate={{ opacity: 1, scale: 1 }}
 className="mt-8 p-8 rounded-2xl bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 text-center"
 >
 <h3 className="text-2xl font-display font-bold text-amber-600 dark:text-[#3366cc] mb-4">
 Aravot - The Highest Heaven
 </h3>
 <p className="hebrew-text text-xl mb-4">ערבות</p>
 <p className="text-[var(--color-muted)]">
 You have reached the seventh heaven, the abode of the Throne of Glory.
 Here dwells righteousness, justice, and mercy. The righteous sit crowned,
 basking in the radiance of the Divine Presence.
 </p>
 <div className="mt-6 p-4 bg-white/10 dark:bg-black/20 rounded-lg">
 <p className="hebrew-text text-lg text-primary-600 dark:text-primary-400">
 ברוך כבוד ה׳ ממקומו
 </p>
 <p className="text-sm text-[var(--color-muted)] mt-2">
"Blessed is the Glory of the Lord from His place"
 </p>
 </div>
 </motion.div>
 )}
 </motion.div>
 </AnimatePresence>
 </div>
 </div>
 </motion.div>
 )}
 </AnimatePresence>

 {/* Sefirot Tree - Divine Emanations */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.45 }}
 className="mt-16 max-w-5xl mx-auto relative z-10"
 >
 <SefirotTree />
 </motion.div>

 {/* Hekhalot Literature */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.5 }}
 className="mt-16 max-w-4xl mx-auto relative z-10"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 The <span className="text-[#202122] font-bold font-serif">Hekhalot Tradition</span>
 </h2>

 <div className="card transition-all duration-300 hover:shadow-lg">
 <p className="text-[var(--color-muted)] mb-6">
 The seven heavens tradition is elaborated in the Hekhalot ("Palaces") literature,
 mystical texts from late antiquity that describe in vivid detail the journey
 of the soul through celestial realms. The mystic (yored merkavah -"one who
 descends to the chariot") must pass through seven palaces, guarded by fearsome
 angels who demand passwords and seals.
 </p>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div className="p-4 rounded-lg bg-primary-600/5 transition-all duration-300 hover:bg-primary-600/10">
 <h4 className="font-semibold mb-2">Requirements for the Journey</h4>
 <ul className="text-sm text-[var(--color-muted)] space-y-1">
 <li>* Mastery of Torah and halakha</li>
 <li>* Moral purity and ethical conduct</li>
 <li>* Knowledge of angelic names and seals</li>
 <li>* Proper intention and preparation</li>
 </ul>
 </div>
 <div className="p-4 rounded-lg bg-secondary-700/5 transition-all duration-300 hover:bg-secondary-700/10">
 <h4 className="font-semibold mb-2">Dangers of the Journey</h4>
 <ul className="text-sm text-[var(--color-muted)] space-y-1">
 <li>* Hostile angels testing worthiness</li>
 <li>* Overwhelming divine radiance</li>
 <li>* Loss of self in cosmic vastness</li>
 <li>* Inability to return properly</li>
 </ul>
 </div>
 </div>
 </div>
 </motion.div>
 </div>
 );
}
